/*
 * A place for miscellaneous constants
 */
import { DeepWriteable } from "../utils/ts-helpers";

type CountryCodeConversions = {
  [key: string]: CountryCodeConversionsObj;
};
type CountryCodeConversionsObj = {
  name: string;
  displayName: string;
  sortKey: number;
};

export type parsedSubmission = DeepWriteable<RecentBrandApproval> & {
  submissionTypes: string[];
  indications: string[];
  diseaseStates: string[];
  relatedSubmissions?: parsedSubmission[];
  strengths?: string[];
};

export type PendingGenericApproval = GatsbyTypes.IPDPlatform_GenericLaunch_Int & {
  relatedApprovals?: Array<
    GatsbyTypes.IPDPlatform_GenericLaunch_Int & {
      isFavorite?: boolean | undefined;
    }
  >;
};

type RecentBrandApproval_Int = NonNullable<
  NonNullable<GatsbyTypes.RecentBrandSubmissionsQuery["ipdPlatform"]["recentApprovedSubmissions_Int"]>[0]
>;

export type RecentBrandApprovalApprovalProgramType =
  | GatsbyTypes.IPDPlatform_DrugIndication_ApprovalProgramType
  | GatsbyTypes.IPDPlatform_ApprovalProgramType;

export type RecentBrandApproval = {
  id: string;
  submissionType: RecentBrandApproval_Int["submissionType"];
  statusEvents: RecentBrandApproval_Int["statusEvents"];
  indication: Omit<RecentBrandApproval_Int["indication"], "approvals"> & {
    approvals: Array<{
      approvalProgram: RecentBrandApprovalApprovalProgramType;
      statusEvents?: RecentBrandApproval_Int["indication"]["approvals"][0]["statusEvents"];
    }>;
    region?: RecentBrandApproval_Int["indication"]["region"];
  };
};

export const CountryCodeConversions: CountryCodeConversions = {
  fr: { name: "FR", displayName: "France", sortKey: 2 },
  it: { name: "IT", displayName: "Italy", sortKey: 4 },
  gr: { name: "DE", displayName: "Germany", sortKey: 3 },
  uk: { name: "UK", displayName: "UK", sortKey: 1 },
  sp: { name: "ES", displayName: "Spain", sortKey: 6 },
};

export const SMALL_COLUMN_WIDTH = 110;
export const JAPAN_ENTRANT_NUMBER_TOOLTIP_TEXT = `In Japan, many generics launch on
            the same day. As such, we have assigned the same entrant number to all generics that launch
            on the same day.`;
export const CANADA_DATE_ACCEPTED_TOOLTIP_TEXT = `Date submission was accepted for review.`;
export const CANADA_SCHEDULE_DATE_ACCEPTED_COLUMN_HEADER_TEXT = "Stage (Date Accepted)";
export const GENERIC_APPROVAL_UNVERIFIED_TOOLTIP_TEXT = `This information is from third party sources that we are not able to independently verify`;
export const KOREA_STAGE_DATE_TOOLTIP_TEXT =
  "Pending date indicates the drug filing date. Phase III date indicates the protocol approval date.";
export const KOREA_PENDING_BRAND_APPROVALS_SCHEDULE_STAGE_DATE_COLUMN_HEADER_TEXT = "Stage (Date)";
export const USA_PENDING_BRAND_APPROVALS_SOURCE_SUBMISSION_TYPE_HEADER_TEXT = "Sub.";
export const USA_PENDING_BRAND_APPROVALS_SOURCE_SUBMISSION_TYPE_TOOLTIP_TEXT = "Submission Type";
export const JP_PROJECTED_GENERIC_LAUNCHES_EST_LOE_TOOLTIP_TEXT =
  "Loss of Exclusivity (LOE) is the estimated date on which a branded product may lose its exclusivity such as key patents and regulatory exclusivities.  Earliest Generic Launch is the estimated date on which a branded product may face generic competition after taking necessary regulatory steps to launch products in Japan.  Our analysts estimate LOE and Earliest Generic Launch dates for a branded product after analyzing the relevant patents, regulatory exclusivities, and other publicly-available information.";
export const PROJECTED_GENERIC_LAUNCHES_EST_LOE_TOOLTIP_TEXT =
  "Loss of Exclusivity (LOE) is the estimated date on which a branded product may lose its exclusivity based on patents and regulatory exclusivities. Our analysts estimate LOE dates for a branded product after analyzing the relevant patents, regulatory exclusivities, and other publicly-available information.";
export const JP_RECENT_GENERIC_LAUNCHES_ENTRANT_TOOLTIP_TEXT =
  "In Japan, many generics launch on the same day. As such, we have assigned the same entrant number to all generics that launch on the same day.";
export const RECENT_GENERIC_LAUNCHES_PRICE_LISTED_DATE_TOOLTIP_TEXT =
  "Drugs without Price Listing Dates are not covered by NHI drug price standards.";
export const JP_RECENT_GENERIC_APPROVALS_ENTRANT_TOOLTIP_TEXT =
  "In Japan, many generics launch on the same day. As such, we have assigned the same entrant number to all generics that launch on the same day.";
export const RECENT_GENERIC_APPROVALS_PRICE_LISTED_DATE_TOOLTIP_TEXT =
  "Drugs without Price Listing Dates are not covered by NHI drug price standards.";
